import React from "react";
import { Heart, Wrench, Leaf, Clock } from "lucide-react";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1564435147551-fcd1d3d773f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxyZWQlMjBjYXIlMjBzaGlueSUyMGNsZWFufGVufDB8fHx8MTc4MTc5NTg3OXww&ixlib=rb-4.1.0&q=85";

const VALUES = [
  {
    icon: Wrench,
    label: "Pro Equipment",
    desc: "Commercial-grade wands, foam brushes, and spot-free rinse systems",
  },
  {
    icon: Heart,
    label: "Locally Owned",
    desc: "A family business built around our community",
  },
  {
    icon: Leaf,
    label: "Eco-Friendly",
    desc: "Biodegradable soaps that protect your car and the planet",
  },
  {
    icon: Clock,
    label: "Extended Hours",
    desc: "Wash on your own schedule — we're open 7 days a week",
  },
];

export default function About() {
  return (
    <section id="about" className="py-24 bg-[#f0f9ff]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full bg-[#a8d8ea]/25 rounded-3xl" />
            <img
              src={ABOUT_IMAGE}
              alt="A clean, shiny car after a self-service wash"
              className="relative rounded-3xl w-full h-[460px] object-cover shadow-2xl"
              data-testid="about-image"
            />
            <div className="absolute -bottom-6 -right-4 bg-white rounded-2xl p-5 shadow-xl border border-[#a8d8ea]/40">
              <div
                className="text-3xl font-bold text-[#1a2744]"
                style={{ fontFamily: "Fredoka, sans-serif" }}
              >
                5.0
              </div>
              <div className="text-xs text-[#1a2744]/60 font-semibold mt-0.5">
                Customer Rating
              </div>
              <div className="flex gap-0.5 mt-1 text-[#a8d8ea]">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-sm font-bold">★</span>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-[#a8d8ea]/40 text-[#1a2744] rounded-full px-4 py-2 text-sm font-bold mb-6 border border-[#a8d8ea]/40">
              <Heart size={15} strokeWidth={2.5} className="text-[#1a2744]" />
              Our Story
            </div>
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#1a2744] mb-6"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="about-heading"
            >
              Professional Equipment. Total Control.
            </h2>
            <p className="text-[#1a2744]/70 text-base sm:text-lg leading-relaxed mb-5">
              At Liv's Car Wash, we believe you know your car best. That's why we
              give you professional-grade self-service bays with everything you need
              to get the job done right — no waiting for staff, no rushing. Just
              you, your car, and the tools to make it shine.
            </p>
            <p className="text-[#1a2744]/70 text-base sm:text-lg leading-relaxed mb-10">
              We're locally owned and keep our bays spotless so every wash is as
              good as the last. Our eco-friendly products are tough on dirt but
              gentle on your paint — and our planet.
            </p>

            <div className="grid grid-cols-2 gap-5">
              {VALUES.map(({ icon: Icon, label, desc }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-[#a8d8ea]/40 shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
                  data-testid={`about-value-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#a8d8ea]/30 flex items-center justify-center">
                    <Icon size={18} strokeWidth={2.5} className="text-[#1a2744]" />
                  </div>
                  <div>
                    <div className="font-bold text-[#1a2744] text-sm">{label}</div>
                    <div className="text-[#1a2744]/55 text-xs mt-0.5 leading-snug">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
